// src/components/shopper/ShopperCart.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  IconButton,
  List,
  ListItem,
  Divider,
  Popover,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';

// ——— CART CONTEXT & HOOK ———
const CartContext = createContext();

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const stored = localStorage.getItem('myCart');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem('myCart', JSON.stringify(items));
  }, [items]);

  const addItem = (product, qty) => {
    setItems(curr => {
      const exists = curr.find(i => i.id === product.id);
      if (exists) {
        return curr.map(i =>
          i.id === product.id ? { ...i, qty: i.qty + qty } : i
        );
      }
      return [...curr, { ...product, qty }];
    });
  };

  const decreaseItem = id => {
    setItems(curr =>
      curr.flatMap(i => {
        if (i.id === id) {
          if (i.qty > 1) {
            return { ...i, qty: i.qty - 1 };
          } else {
            return []; // remove if qty would go to zero
          }
        }
        return i;
      })
    );
  };

  const removeItem = id => {
    setItems(curr => curr.filter(i => i.id !== id));
  };

  const totalPrice = items.reduce((sum, item) => {
    const priceNum = parseFloat(item.price.replace(/[^\d.]/g, '')) || 0;
    return sum + priceNum * item.qty;
  }, 0);

  return (
    <CartContext.Provider value={{ items, addItem, decreaseItem, removeItem, totalPrice }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  return useContext(CartContext);
}

// ——— DRAW-DOWN MINI-CART ———
export function CartDropdown({ anchorEl, open, onClose }) {
  const theme = useTheme();
  const { items, removeItem, totalPrice } = useCart();

  return (
    <Popover
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      <Box sx={{ p: 2, width: 300 }}>
        <Typography variant="h6" gutterBottom>
          My Cart
        </Typography>

        {items.length === 0 ? (
          <Typography>Your cart is empty.</Typography>
        ) : (
          <>
            <List disablePadding>
              {items.map(item => (
                <ListItem
                  key={item.id}
                  sx={{
                    py: 1,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {/* Thumbnail */}
                  <Box
                    component="img"
                    src={item.img}
                    alt={item.name}
                    sx={{
                      width: 40,
                      height: 40,
                      objectFit: 'cover',
                      borderRadius: 1,
                      mr: 1.5,
                    }}
                  />

                  {/* Name & qty × price */}
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="body2">{item.name}</Typography>
                    <Typography variant="caption">
                      {item.qty} × {item.price}
                    </Typography>
                  </Box>

                  {/* Remove item */}
                  <IconButton size="small" onClick={() => removeItem(item.id)}>
                    ×
                  </IconButton>
                </ListItem>
              ))}
            </List>

            <Divider sx={{ my: 1 }} />

            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Total: €{totalPrice.toFixed(2)}
            </Typography>

            <Button
              variant="contained"
              color="primary"
              component={RouterLink}
              to="/checkout"
              fullWidth
            >
              Checkout
            </Button>
          </>
        )}
      </Box>
    </Popover>
  );
}

// ——— FULL-PAGE CART / CHECKOUT ———
export function ShopperCart() {
  const theme = useTheme();
  const nav = useNavigate();
  const { items, addItem, decreaseItem, removeItem, totalPrice } = useCart();

  return (
    <Container component="main" sx={{ py: 4, minHeight: '90vh' }}>
      <Typography variant="h4" gutterBottom>
        Your Shopping Cart
      </Typography>

      {items.length === 0 ? (
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography>Your cart is empty.</Typography>
          <Button variant="contained" sx={{ mt: 2 }} onClick={() => nav('/')}>
            Back to Shop
          </Button>
        </Box>
      ) : (
        <Box>
          {items.map(item => (
            <Box
              key={item.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                mb: 2,
                p: 2,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
              }}
            >
              <Box
                component="img"
                src={item.img}
                alt={item.name}
                sx={{ width: 80, height: 80, objectFit: 'cover', mr: 2 }}
              />
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="body1">{item.name}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                  <IconButton size="small" onClick={() => decreaseItem(item.id)}>
                    <RemoveIcon fontSize="small" />
                  </IconButton>
                  <Typography variant="body2" sx={{ mx: 1 }}>
                    {item.qty}
                  </Typography>
                  <IconButton size="small" onClick={() => addItem(item, 1)}>
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  Unit: {item.price} | Subtotal: €
                  {(parseFloat(item.price.replace(/[^\d.]/g, '')) * item.qty).toFixed(2)}
                </Typography>
              </Box>
              <IconButton onClick={() => removeItem(item.id)}>×</IconButton>
            </Box>
          ))}

          <Divider sx={{ my: 2 }} />

          <Typography variant="h6" gutterBottom>
            Total: €{totalPrice.toFixed(2)}
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => nav('/checkout')}
            >
              Checkout
            </Button>
            <Button variant="outlined" onClick={() => nav('/')}>
              Continue Shopping
            </Button>
          </Box>
        </Box>
      )}
    </Container>
  );
}

export default ShopperCart;
